import { API, deleteRequest, getRequest } from '@/api/api';
import type { EventResponse, EventsListResponse } from '@/store/events/events.dto';

import type { GalleryDeleteScope, GalleryImageDto, GalleryScope } from './gallery.dto';
import { mapGalleryImage } from './gallery.mappers';

/**
 * One whole gallery space. The service returns it unpaged; `event_id` and
 * `without_event` narrow it, but every screen here groups one read client-side
 * rather than asking once per event.
 */
export const getGalleryImages = async (scope: GalleryScope) => {
  const response = await getRequest<GalleryImageDto[] | null>(
    API.ACTIVITY,
    `/gallery?scope=${scope}`,
  );
  return (response.data ?? []).map(mapGalleryImage);
};

/** The file itself goes only when no gallery links to it any more. */
export const deleteGalleryImage = async (id: string, scope: GalleryDeleteScope) => {
  await deleteRequest(API.ACTIVITY, `/gallery/${id}?scope=${scope}`);
};

/**
 * The events a photo may be added to right now: the ones on the reader's roster
 * that have not ended.
 *
 * Mirrors what the service accepts rather than what the calendar shows. An
 * upload into an event is refused unless the uploader is one of its participants
 * — which is exactly what `my_events` matches, for an owner too — and a photo
 * filed under a sub-activity lands in its parent, so only parents are offered.
 * An event's gallery takes no uploads once the event has ended; the status
 * filter drops completed and cancelled ones, and `hasEnded` catches one whose
 * end has passed before its status caught up.
 */
export const getGalleryUploadEvents = async (): Promise<EventResponse[]> => {
  const response = await getRequest<EventsListResponse>(
    API.ACTIVITY,
    '/events?limit=1000&page=1&my_events=true&only_parent_events=true&status=planned&status=underway',
  );
  const now = Date.now();
  return (response.data.items ?? []).filter((event) => !hasEnded(event, now));
};

/** An idea with no dates has not ended, and neither has an open-ended event. */
const hasEnded = (event: EventResponse, now: number) =>
  !!event.time_slot?.end && new Date(event.time_slot.end).getTime() < now;
